import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { getRceCustomerCategory } from 'utils/getRceCustomerCategory'
import { isRceEditConfig } from 'config/utils/config'
import { useGate } from 'gates'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { getRCEVoucherExperiment } from '../helpers/voucherExperiment'
import { resolveRCEVoucherTopCandidate } from '../helpers/resolveRCEVoucherTopCandidate'
import { resolveStandardVoucherTopCandidate } from '../helpers/resolveStandardVoucherTopCandidate'
import { resolveVoucherTopBanner } from '../helpers/resolveVoucherTopBanner'
import type {
  VoucherBannerHiddenReason,
  VoucherTopBannerModel
} from '../VoucherBanner.types'
import { trackTermsAndConditions } from '../helpers/trackTermsAndConditions'
import { RCE_VOUCHER_ACTIVATED_NOTICE_PAGES } from '../VoucherBanner.constants'
import useRCEVoucherBanner from './useRCEVoucherBanner'
import useVoucherBanner from './useVoucherBanner'
import useVoucherBannerTracking from './useVoucherBannerTracking'
import { useMemo } from 'react'

interface UseVoucherBannerControllerReturn {
  model: VoucherTopBannerModel
  activateRCEVoucher: () => void
  trackTerms: () => void
}

const useVoucherBannerController = (): UseVoucherBannerControllerReturn => {
  const standardState = useVoucherBanner()
  const rceState = useRCEVoucherBanner()
  const { rootStore } = useRootStore()

  const {
    urlParams,
    traversalSession,
    page: { config },
    navigation: { currentPageInSession, parentUrl },
    traversal: { data }
  } = rootStore

  const selectedRCEExperiment = getRCEVoucherExperiment(
    getRceCustomerCategory({
      utm_campaign: urlParams.utm_campaign,
      parentUrl
    })
  )

  // Experiment resolution/control checks are resolved via inline gate
  // predicates (see docs/skills/experiments.md) so this hook consumes only
  // `useGate()` results instead of reading `experimentData` on its own.
  const isRCEVoucherResolved = useGate((ctx) => {
    if (!isRceEditConfig(ctx.configName)) return false

    const experiment = getRCEVoucherExperiment(ctx)
    return experiment !== null && ctx.experimentData[experiment] !== undefined
  })
  const isRCEVoucherControl = useGate((ctx) => {
    if (!isRceEditConfig(ctx.configName)) return false

    const experiment = getRCEVoucherExperiment(ctx)
    if (!experiment) return false

    const variant = ctx.experimentData[experiment]
    return (
      variant !== PossibleVariants.VARIATION_B &&
      variant !== PossibleVariants.VARIATION_C
    )
  })
  const isSmavaVoucherVarB = useGate('cat4707SmavaVoucherVarB')

  // rceHiddenReason is for observability only (tracking/debugging why the
  // RCE voucher banner is hidden) and doesn't drive any control logic.
  const rceHiddenReason: VoucherBannerHiddenReason = useMemo(() => {
    if (!selectedRCEExperiment) return 'flow-ineligible'
    if (!isRCEVoucherResolved) return 'experiment-unresolved'
    if (isRCEVoucherControl) return 'control'
    if (
      currentPageInSession !== null &&
      RCE_VOUCHER_ACTIVATED_NOTICE_PAGES.includes(currentPageInSession) &&
      !rceState.isVoucherActivated
    )
      return 'activation-required'

    return 'unsupported-page'
  }, [
    selectedRCEExperiment,
    isRCEVoucherResolved,
    isRCEVoucherControl,
    currentPageInSession,
    rceState.isVoucherActivated
  ])

  const standardFlow = useMemo(() => {
    const standard = resolveStandardVoucherTopCandidate({
      isEligible: standardState.isVoucherBannerVisible,
      page: currentPageInSession,
      amount: standardState.voucherAmount,
      variant: standardState.voucherBannerVariant,
      isActivated: standardState.isVoucherActivated
    })

    if (isSmavaVoucherVarB && standard.kind === 'standard-contact-prompt') {
      return { ...standard, kind: 'standard-contact-prompt-var-b' as const }
    }

    if (isSmavaVoucherVarB && standard.kind === 'standard-activated') {
      return { ...standard, kind: 'standard-activated-var-b' as const }
    }

    return standard
  }, [
    standardState.isVoucherBannerVisible,
    currentPageInSession,
    standardState.voucherAmount,
    standardState.voucherBannerVariant,
    standardState.isVoucherActivated,
    isSmavaVoucherVarB
  ])

  const rce = resolveRCEVoucherTopCandidate({
    presentation: rceState.presentation,
    isActivated: rceState.isVoucherActivated,
    hiddenReason: rceHiddenReason
  })
  const model = resolveVoucherTopBanner({
    configName: config.name,
    isReady: config.version !== false && currentPageInSession !== null,
    standard: standardFlow,
    rce
  })
  const isRCEFlow = isRceEditConfig(config.name)
  const sourceAmount = isRCEFlow
    ? traversalSession.rceEditInitialData.amount || 0
    : data.common.amount
  const decisionKey = isRCEFlow
    ? `${selectedRCEExperiment || 'no-experiment'}:${
        isRCEVoucherResolved ? rceState.variant : 'pending'
      }`
    : standardState.voucherBannerVariant

  useVoucherBannerTracking({
    model,
    flow: isRCEFlow ? 'rce' : 'standard',
    sourceAmount,
    decisionKey
  })

  return {
    model,
    activateRCEVoucher: rceState.activateVoucher,
    trackTerms: () => trackTermsAndConditions(data)
  }
}

export default useVoucherBannerController
